// src/pages/TherapistDetailPage.js
import React, { useEffect, useState } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import {
  collection,
  getDocs,
  query,
  where,
  doc,
  getDoc,
} from 'firebase/firestore'
import { db } from '../firebaseConfig'
import {
  ArrowLeft,
  User,
  Phone,
  Mail,
  Star,
  MapPin,
  Loader2,
  Wallet,
  CheckCircle,
  XCircle,
} from 'lucide-react'

export default function TherapistDetailPage() {
  const { mitraId } = useParams()
  const navigate = useNavigate()
  const [therapist, setTherapist] = useState(null)
  const [reviews, setReviews] = useState([])
  const [loading, setLoading] = useState(true)
  const [loadingReviews, setLoadingReviews] = useState(true)
  const [error, setError] = useState(null)

  // ============================================================
  // 🔹 FETCH DATA THERAPIST
  // ============================================================
  useEffect(() => {
    const fetchTherapist = async () => {
      const safeId = (mitraId || '').trim()
      if (!safeId || safeId.length < 4) {
        console.warn('⚠️ mitraId tidak valid:', safeId)
        setError('ID mitra tidak valid.')
        return
      }

      try {
        setLoading(true)
        setError(null)
        console.log('🔍 Fetching therapist for id_number:', safeId)

        const q = query(
          collection(db, 'therapists'),
          where('id_number', '==', safeId)
        )
        const snapshot = await getDocs(q)

        if (!snapshot.empty) {
          const data = snapshot.docs[0].data()
          setTherapist({ id: snapshot.docs[0].id, ...data })
          console.log('✅ Data therapist ditemukan:', data)
        } else {
          // fallback jika doc.id == id_number
          const docRef = doc(db, 'therapists', safeId)
          const docSnap = await getDoc(docRef)
          if (docSnap.exists()) {
            setTherapist({ id: docSnap.id, ...docSnap.data() })
            console.log('✅ Ditemukan berdasarkan doc.id:', docSnap.data())
          } else {
            console.error('❌ Therapist tidak ditemukan di Firestore.')
            setError(`Data therapist dengan ID ${safeId} tidak ditemukan.`)
          }
        }
      } catch (err) {
        console.error('🔥 Error mengambil data therapist:', err)
        setError('Terjadi kesalahan saat memuat data therapist.')
      } finally {
        setLoading(false)
      }
    }

    if (mitraId) fetchTherapist()
  }, [mitraId])

  // ============================================================
  // 🔹 FETCH ULASAN PENGGUNA
  // ============================================================
  useEffect(() => {
    const fetchReviews = async () => {
      const safeId = (mitraId || '').trim()
      if (!safeId || safeId.length < 4) {
        console.warn('⚠️ mitraId tidak valid, ulasan tidak diambil.')
        return
      }

      try {
        setLoadingReviews(true)
        console.log('💬 Mengambil ulasan untuk terapis:', safeId)

        const q = query(
          collection(db, 'ulasan'),
          where('mitraId', '==', safeId),
          where('mitraType', '==', 'therapist')
        )
        const snap = await getDocs(q)
        const data = snap.docs.map((d) => ({
          id: d.id,
          ...d.data(),
        }))

        console.log(`💬 ${data.length} ulasan ditemukan untuk ${safeId}`)
        setReviews(data)
      } catch (err) {
        console.error('❌ Gagal memuat ulasan:', err)
      } finally {
        setLoadingReviews(false)
      }
    }

    if (mitraId) fetchReviews()
  }, [mitraId])

  // ============================================================
  // 🔹 UI STATES
  // ============================================================
  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen text-gray-400">
        <Loader2 className="animate-spin mr-2" /> Memuat data therapist...
      </div>
    )
  }

  if (error) {
    return (
      <div className="p-6 text-center text-red-500 font-medium bg-gray-950 min-h-screen">
        {error}
        <div className="mt-4">
          <button
            onClick={() => navigate(-1)}
            className="px-4 py-2 bg-gray-800 hover:bg-gray-700 rounded-lg text-white"
          >
            ◀ Kembali
          </button>
        </div>
      </div>
    )
  }

  if (!therapist) {
    return (
      <div className="p-6 text-center text-gray-400 bg-gray-950 min-h-screen">
        Tidak ada data therapist ditemukan.
      </div>
    )
  }

  // ============================================================
  // 🔹 UI UTAMA
  // ============================================================
  return (
    <div className="min-h-screen bg-gray-950 text-gray-200 p-6">
      {/* HEADER */}
      <div className="flex items-center justify-between mb-6">
        <button
          onClick={() => navigate(-1)}
          className="flex items-center gap-2 px-3 py-2 bg-gray-800 hover:bg-gray-700 border border-gray-700 rounded-lg"
        >
          <ArrowLeft size={18} /> Kembali
        </button>
        <h2 className="text-2xl font-bold text-blue-400">
          💆 Detail Therapist — {therapist.name || 'Tanpa Nama'}
        </h2>
      </div>

      {/* DATA THERAPIST */}
      <div className="bg-gray-900 border border-gray-800 rounded-xl p-5 mb-6 shadow-lg">
        <div className="flex flex-col md:flex-row items-center gap-5">
          <img
            src={therapist.photoUrl}
            alt={therapist.name}
            className="w-28 h-28 rounded-full border border-gray-700 object-cover"
          />
          <div className="flex-1">
            <h3 className="text-xl font-semibold mb-1">{therapist.name}</h3>
            <p className="text-gray-400 text-sm mb-3">
              ID:{' '}
              <span className="text-gray-300 font-mono">
                {therapist.id_number}
              </span>
            </p>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
              <p className="flex items-center gap-2">
                <Phone size={14} /> {therapist.phone || '-'}
              </p>
              <p className="flex items-center gap-2">
                <Mail size={14} /> {therapist.email || '-'}
              </p>
              <p className="flex items-center gap-2">
                <User size={14} /> Gender: {therapist.gender || '-'}
              </p>
              <p className="flex items-center gap-2">
                <Star size={14} /> Rating:{' '}
                <span className="text-yellow-400">
                  {Number(therapist.rating || 0).toFixed(1)}
                </span>
              </p>
              <p className="flex items-center gap-2">
                <MapPin size={14} />{' '}
                {Array.isArray(therapist.cities) && therapist.cities.length > 0
                  ? therapist.cities.join(', ')
                  : '-'}
              </p>
              <p className="flex items-center gap-2">
                <Wallet size={14} /> Favorit:{' '}
                <span className="text-green-400 font-semibold">
                  {Number(therapist.favoriteCount || 0).toLocaleString('id-ID')}
                </span>
              </p>
              <p className="flex items-center gap-2">
                Status:{' '}
                {therapist.status ? (
                  <span className="flex items-center gap-1 text-green-400">
                    <CheckCircle size={14} /> Aktif
                  </span>
                ) : (
                  <span className="flex items-center gap-1 text-red-400">
                    <XCircle size={14} /> Nonaktif
                  </span>
                )}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* LAYANAN */}
      <div className="bg-gray-900 border border-gray-800 rounded-xl p-5 mb-8 shadow-lg">
        <h3 className="text-lg font-semibold mb-3 text-blue-400">
          🧾 Layanan & Varian
        </h3>

        {Array.isArray(therapist.services) && therapist.services.length > 0 ? (
          <div className="flex flex-wrap gap-2">
            {therapist.services.map((svc, index) => (
              <div
                key={index}
                className="bg-gray-800 border border-gray-700 rounded-lg px-3 py-2 text-sm"
              >
                <p className="font-medium text-gray-300">{svc.serviceId}</p>
                {Array.isArray(svc.variants) && svc.variants.length > 0 ? (
                  <p className="text-xs text-gray-400 mt-1">
                    Varian: {svc.variants.join(', ')}
                  </p>
                ) : (
                  <p className="text-xs text-gray-500 italic">
                    Tidak ada varian
                  </p>
                )}
              </div>
            ))}
          </div>
        ) : (
          <p className="text-gray-400 italic text-sm">Tidak ada layanan.</p>
        )}
      </div>

      {/* ULASAN */}
      <div className="bg-gray-900 border border-gray-800 rounded-xl p-5 shadow-lg">
        <h3 className="text-lg font-semibold mb-4 text-blue-400">
          💬 Rating & Ulasan Pengguna
        </h3>

        {loadingReviews ? (
          <div className="flex items-center text-gray-400">
            <Loader2 size={16} className="animate-spin mr-2" /> Memuat ulasan...
          </div>
        ) : reviews.length === 0 ? (
          <p className="text-gray-400 italic text-sm">
            Belum ada ulasan untuk therapist ini.
          </p>
        ) : (
          <div className="space-y-3 max-h-[400px] overflow-y-auto pr-2">
            {reviews.map((r) => (
              <div
                key={r.id}
                className="border border-gray-800 bg-gray-850 p-3 rounded-lg"
              >
                <div className="flex justify-between items-center mb-1">
                  <p className="font-medium text-gray-200">
                    {r.userName || 'Pengguna'}
                  </p>
                  <p className="text-yellow-400 text-sm flex items-center gap-1">
                    <Star size={14} /> {r.rating?.toFixed(1) || '-'}
                  </p>
                </div>
                <p className="text-gray-400 text-sm italic mb-1">
                  {r.komentar || 'Tidak ada komentar'}
                </p>
                <p className="text-xs text-gray-500">
                  {r.tanggal?.toDate
                    ? new Date(r.tanggal.toDate()).toLocaleString('id-ID')
                    : '-'}
                </p>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* FOOTER */}
      <div className="mt-8 text-xs text-gray-500 text-center">
        Terakhir diperbarui: {new Date().toLocaleString()}
      </div>
    </div>
  )
}
